import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { IDetailsControl } from '../../../shared/models/details-control';
import {
  catchError,
  debounceTime,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  timer,
} from 'rxjs';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { IPlatformUserAccountVm } from '../../../../api/platform-user-account-vm.model';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { PlatformUsersService } from '../../../../api/api-services/platform-users.service';
import { IPaginatedResultPlatformUserAccountVm } from '../../../../api/paginated-result-platform-user-account-vm.model';
import { NewOskService } from '../../../../api/api-services/new-osk.service';
import { OskActivationService } from '../../../../api/api-services/osk-activation.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { eModalRoles } from '../../../shared/models/modals';
import { INewOskIm } from '../../../../api/new-osk-im.model';
import { IOskActivationIm } from '../../../../api/osk-activation-im.model';
import { MessageService } from 'primeng/api';
import { CustomValidators } from '../../../shared/helpers/custom-validators';
import { GusCompanyDataService } from '../../../../api/api-services/gus-company-data.service';
import { OskShortCodeAvailabilityService } from '../../../../api/api-services/osk-short-code-availability.service';

interface IDynamicOSKForm {
  name: keyof INewOskIm;
  label: string;
  type: 'input-string' | 'input-number' | 'input-mask';
  mask?: string;
  fieldWidth?: string;
  fieldTooltip?: string;
  currencyField?: boolean;
  maxFractionDigits?: number;
  minFractionDigits?: number;
}

@Component({
  selector: 'app-osk-add-new',
  templateUrl: './osk-add-new.component.html',
  styleUrl: './osk-add-new.component.scss',
})
export class OskAddNewComponent implements OnInit, OnDestroy {
  private _platformUsersService = inject(PlatformUsersService);
  private _oskActivationService = inject(OskActivationService);
  private _messageService = inject(MessageService);
  private _newOskService = inject(NewOskService);
  private _gusCompanyDataService = inject(GusCompanyDataService);
  private _oskShortCodeAvailabilityService = inject(OskShortCodeAvailabilityService);
  private _dialogRef = inject(DynamicDialogRef);
  private _fb = inject(FormBuilder);

  public mainOSKDataForm!: FormGroup;
  public invoiceOSKForm!: FormGroup;
  public filteredUsers!: IPlatformUserAccountVm[];
  public userOption: 'existing' | 'new' | null = null;
  public emailExists = false;
  public showError = false;
  public showOSKForm = false;
  public invoiceRequested = false;
  public showNotFoundMessage = false;
  public sendInvitationEmail = true;
  public mainDataFormFields: IDynamicOSKForm[] = this.getMainFormFields();
  public invoiceFormFields: IDynamicOSKForm[] = this.getInvoiceFormFields();
  public loading: boolean = false;
  public gusLoading: boolean = false;

  private subscriptions: Subscription[] = [];
  private selectedUser: IPlatformUserAccountVm | null = null;
  private formValidationTriggered = false;

  get mainOSKDataFc(): { [key: string]: FormControl } {
    return this.mainOSKDataForm.controls as { [key: string]: FormControl };
  }

  ngOnInit(): void {
    this.setMainDataFormStructure();
    this.setInvoiceDataFormStructure();
    this.setEmailValueChangesSubscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  closeDialog(status: eModalRoles = eModalRoles.CLOSE) {
    this._dialogRef.close(status);
  }

  updateInvoiceData(): void {
    if (this.invoiceRequested) {
      this.patchInvoiceDataToForm();
    }
  }

  private patchInvoiceDataToForm(): void {
    if (!this.invoiceRequested) return;

    const data: Partial<INewOskIm> = {
      invoiceCompanyName: this.mainOSKDataFc['oskName'].value ?? '',
      invoiceNIP: this.mainOSKDataFc['oskNip'].value ?? '',
      invoiceCity: this.mainOSKDataFc['oskCity'].value ?? '',
      invoiceStreet: this.mainOSKDataFc['oskStreet'].value ?? '',
      invoicePostCode: this.mainOSKDataFc['oskPostCode'].value ?? '',
      invoiceCountry: this.mainOSKDataFc['oskCountry'].value ?? '',
      invoiceEmail: this.mainOSKDataFc['ownerEmail'].value ?? '',
    };
    this.invoiceOSKForm.patchValue(data);
  }

  setMainDataFormStructure(): void {
    const formGroup: any = {};
    this.mainDataFormFields.forEach((field) => {
      formGroup[field.name] = [''];
    });

    this.mainOSKDataForm = this._fb.group({
      ...formGroup,
      ownerEmail: ['', [Validators.required, Validators.email]],
      oskName: ['', [Validators.required]],
      oskShortCode: [
        '',
        [Validators.required, CustomValidators.noSpecialCharsOrSpaces],
        [this.oskShortCodeTakenValidator()],
      ],
    });
  }

  /// Skrócona nazwa identyfikuje OSK (m.in. w publicznym adresie), więc musi być
  /// unikalna — pytamy backend z opóźnieniem, żeby nie strzelać przy każdej literze.
  private oskShortCodeTakenValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = (control.value ?? '').toString().trim();
      if (!value) {
        return of(null);
      }
      return timer(400).pipe(
        switchMap(() => this._oskShortCodeAvailabilityService.get(value)),
        map((res) => (res.available ? null : { shortCodeTaken: true })),
        catchError(() => of(null))
      );
    };
  }

  loadFromGus(): void {
    const nipDigits = (this.mainOSKDataFc['oskNip'].value ?? '')
      .toString()
      .replace(/\D/g, '');
    if (nipDigits.length !== 10) {
      this._messageService.add({
        severity: 'warn',
        summary: 'NIP',
        detail: 'Wpisz poprawny NIP (10 cyfr), aby pobrać dane z GUS.',
      });
      return;
    }

    this.gusLoading = true;
    this._gusCompanyDataService.get(nipDigits).subscribe({
      next: (res) => {
        this.gusLoading = false;
        this.mainOSKDataForm.patchValue({
          oskNip: res.nip ?? nipDigits,
          oskName: res.companyName ?? '',
          oskStreet: res.street ?? '',
          oskPostCode: res.postCode ?? '',
          oskCity: res.city ?? '',
          oskCountry: res.countryCode === 'PL' ? 'Polska' : res.countryCode ?? '',
        });
        this.updateInvoiceData();
        this._messageService.add({
          severity: 'success',
          summary: 'GUS',
          detail: 'Dane firmy zostały pobrane z GUS.',
        });
      },
      error: (err) => {
        this.gusLoading = false;
        this._messageService.add({
          severity: 'error',
          summary: 'GUS',
          detail:
            err?.error?.errorMessage ??
            'Nie udało się pobrać danych z GUS. Spróbuj ponownie lub uzupełnij dane ręcznie.',
        });
      },
    });
  }

  private setInvoiceDataFormStructure(): void {
    const formGroup: any = {};
    this.invoiceFormFields.forEach((field) => {
      formGroup[field.name] = [''];
    });
    this.invoiceOSKForm = this._fb.group(formGroup);
  }

  checkIfEmailExists(): void {
    const emailControl = this.mainOSKDataForm.get('ownerEmail');
    if (
      (emailControl?.invalid && emailControl?.touched) ||
      !emailControl?.value
    ) {
      this.showError = true;
      this.emailExists = false;
      this.showNotFoundMessage = false;
      this.clearDataTakenFromSelectedUser();
      return;
    }
    this.showError = false;
    const email = emailControl.value;
    this.emailExists = this.filteredUsers.some((user) => user.email === email);

    if (this.emailExists) {
      this.showNotFoundMessage = false;
      const user = this.filteredUsers.find((user) => user.email === email);
      if (user) this.patchUserDataToForm(user);
    } else {
      this.showNotFoundMessage = true;
      this.showOSKForm = true;
      this.clearDataTakenFromSelectedUser();
    }
  }

  onEmailSelect(event: AutoCompleteSelectEvent): void {
    this.showError = false;
    this.showNotFoundMessage = false;
    this.patchUserDataToForm(event.value);
    this.mainOSKDataForm.patchValue({
      ownerEmail: event.value.email,
    });
    this.showOSKForm = true;
    this.emailExists = true;
  }

  addUserToOSK(): void {
    if (!this.mainOSKDataForm.valid) {
      if (!this.formValidationTriggered) {
        this.validateForm();
        this.formValidationTriggered = true;
      }
      return;
    }

    this.loading = true;

    const action$ = this.emailExists
      ? this.addExistingUserToOSK()
      : this.addNewUserToOSK();

    action$.subscribe({
      next: () => {
        this.loading = false;
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: this.emailExists
            ? 'Konto użytkownika zostało przypisane do OSK.'
            : 'Utworzono nowe konto użytkownika.',
        });
        this.closeDialog(eModalRoles.SUCCESS);
      },
      error: (err) => {
        this.loading = false;
        console.error(
          this.emailExists ? 'Osk activation error:' : 'Osk creation error:',
          err
        );
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Wystąpił błąd. Spróbuj ponownie później.',
        });
      },
    });
  }

  private addExistingUserToOSK(): Observable<any> {
    const payload: IOskActivationIm = {
      platformUserGuid: this.selectedUser?.guid ?? '',
      ...this.mainOSKDataForm.value,
      invoiceRequested: this.invoiceRequested,
      ...this.invoiceOSKForm.value,
      sendInvitationEmail: this.sendInvitationEmail,
    };
    return this._oskActivationService.post(payload);
  }

  private addNewUserToOSK(): Observable<any> {
    const payload: INewOskIm = {
      ...this.mainOSKDataForm.value,
      invoiceRequested: this.invoiceRequested,
      ...this.invoiceOSKForm.value,
      sendInvitationEmail: this.sendInvitationEmail,
    };
    return this._newOskService.post(payload);
  }

  private fetchUsers(query: string): Observable<IPlatformUserAccountVm[]> {
    return this._platformUsersService
      .get(query, '')
      .pipe(
        map((res: IPaginatedResultPlatformUserAccountVm) => res.data ?? [])
      );
  }

  private setEmailValueChangesSubscription(): void {
    const emailValueChangesSubscription = this.mainOSKDataForm
      .get('ownerEmail')!
      .valueChanges.pipe(
        debounceTime(300), // Add debounce to limit API calls
        switchMap((query) => {
          if (!query) {
            return of([]); // Return empty array if query is empty
          }
          return this.fetchUsers(query); // Fetch suggestions from API
        })
      )
      .subscribe((users) => {
        this.filteredUsers = users;
        if (this.showOSKForm) {
          this.checkIfEmailExists();
        }
      });

    this.subscriptions.push(emailValueChangesSubscription);
  }

  private patchUserDataToForm(user: IPlatformUserAccountVm): void {
    this.selectedUser = JSON.parse(JSON.stringify(user));

    const data: Partial<INewOskIm> = {
      oskName: user.companyName ?? '',
      oskNip: '',
      oskStreet: user.street ?? '',
      oskPostCode: user.postCode ?? '',
      oskCity: user.city ?? '',
      oskPhones: user.phone ?? '',
      oskCountry: user.country ?? '',
    };
    this.mainOSKDataForm.patchValue(data);
  }

  private validateForm(): void {
    Object.entries(this.mainOSKDataForm.controls).forEach(([key, control]) => {
      if (key !== 'ownerEmail' && control) {
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
      }
    });
  }

  /**
   * Clears only what was copied from a previously matched platform account. The owner e-mail is
   * checked against the API on every keystroke, and wiping the whole form on a miss threw away
   * everything the operator had typed by hand - the OSK e-mail, NIP or www address included.
   */
  private clearDataTakenFromSelectedUser(): void {
    if (!this.selectedUser) {
      return;
    }

    this.selectedUser = null;
    this.mainOSKDataForm.patchValue({
      oskName: '',
      oskNip: '',
      oskStreet: '',
      oskPostCode: '',
      oskCity: '',
      oskPhones: '',
      oskCountry: '',
    });

    if (this.invoiceRequested) {
      this.patchInvoiceDataToForm();
    }
  }

  private getMainFormFields(): IDynamicOSKForm[] {
    return [
      {
        name: 'oskNip',
        label: 'NIP',
        type: 'input-string',
        fieldWidth: 'w-full',
        fieldTooltip:
          'Podaj NIP i wczytaj dane firmy z GUS, aby nie przepisywać ich ręcznie.',
      },
      {
        name: 'oskName',
        label: 'Pełna nazwa OSK',
        type: 'input-string',
        fieldWidth: 'w-full',
      },
      {
        name: 'oskShortCode',
        label: 'Skrócona nazwa',
        type: 'input-string',
        fieldWidth: 'w-full',
      },
      {
        name: 'ownerFirstName',
        label: 'Imię',
        type: 'input-string',
        fieldTooltip: 'Imię użytkownika konta OSK',
      },
      {
        name: 'ownerLastName',
        label: 'Nazwisko',
        type: 'input-string',
        fieldTooltip: 'Nazwisko użytkownika konta OSK',
      },
      { name: 'oskStreet', label: 'Ulica', type: 'input-string' },
      {
        name: 'oskPostCode',
        label: 'Kod pocztowy',
        type: 'input-mask',
        mask: '99-999',
      },
      { name: 'oskCity', label: 'Miasto', type: 'input-string' },
      { name: 'oskCountry', label: 'Kraj', type: 'input-string' },
      { name: 'oskPhones', label: 'Numer telefonu', type: 'input-string' },
      {
        name: 'oskEmail',
        label: 'Adres e-mail OSK',
        type: 'input-string',
        fieldWidth: 'w-full',
        fieldTooltip: 'Adres email, który będzie dostępny publicznie.',
      },
      {
        name: 'oskUrl',
        label: 'Adres strony www',
        type: 'input-string',
        fieldWidth: 'w-full',
      },
    ];
  }

  private getInvoiceFormFields(): IDynamicOSKForm[] {
    return [
      {
        name: 'invoiceCompanyName',
        label: 'Nazwa firmy',
        type: 'input-string',
        fieldWidth: 'w-full',
      },
      { name: 'invoiceNIP', label: 'NIP', type: 'input-string' },
      { name: 'invoiceEmail', label: 'Adres email', type: 'input-string' },
      { name: 'invoiceStreet', label: 'Ulica', type: 'input-string' },
      {
        name: 'invoicePostCode',
        label: 'Kod pocztowy',
        type: 'input-mask',
        mask: '99-999',
      },
      { name: 'invoiceCity', label: 'Miasto', type: 'input-string' },
      { name: 'invoiceCountry', label: 'Kraj', type: 'input-string' },
    ];
  }
}
